import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { apiGetJson as apiGet, apiPatchJson as apiPatch } from '../../../../../../utils/api';
import CompFilters from './CompFilters';
import CandidatesTable from './CandidatesTable';

// A client is eligible if it had file-review activity within N months (and,
// optionally, a contact who's logged into the portal). Both are admin-tunable
// SystemSettings — change them here, no deploy needed.
function EligibilityRules({ eligibility, onChanged }) {
  const [months, setMonths] = useState(eligibility?.window_months ?? 12);
  const [saving, setSaving] = useState(false);

  // Re-sync the input if the active rule changes (after a save, or another admin
  // updates it) so it never shows a stale value.
  useEffect(() => {
    if (eligibility?.window_months != null) setMonths(eligibility.window_months);
  }, [eligibility?.window_months]);

  const save = async (key, value) => {
    setSaving(true);
    try {
      await apiPatch(`/service_comp/eligibility/${key}`, { value: String(value) });
      toast.success('Eligibility rule updated');
      onChanged();
    } catch { toast.error('Could not update setting'); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
      <span className="text-sm font-medium dark:text-gray-200">Eligible if active within</span>
      <input
        type="number" min="1" max="120" value={months} disabled={saving}
        onChange={(e) => setMonths(e.target.value)}
        onBlur={() => Number(months) > 0 && Number(months) !== eligibility?.window_months && save('service_comp_eligibility_months', Number(months))}
        onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        aria-label="Eligibility window in months"
        className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-900 dark:text-white"
      />
      <span className="text-sm dark:text-gray-200">months</span>
      <label className="flex items-center gap-1.5 text-sm dark:text-gray-200 ml-2">
        <input
          type="checkbox" checked={!!eligibility?.require_login} disabled={saving}
          onChange={(e) => save('service_comp_require_login', e.target.checked)}
        />
        Require a portal login
      </label>
    </div>
  );
}

// Admin review of File Review clients eligible for complimentary JPRO access.
export default function FileReviewCompsTab() {
  const [filters, setFilters] = useState({ status: '', eligible: false, standalone: false, q: '' });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['service-comp-candidates', filters],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filters.status) p.set('status', filters.status);
      if (filters.eligible) p.set('eligible', 'true');
      if (filters.standalone) p.set('standalone', 'true');
      return apiGet(`/admin/service_comp_candidates?${p.toString()}`);
    },
  });

  const all = data?.candidates || [];
  const candidates = filters.q
    ? all.filter((c) => (c.company_name || '').toLowerCase().includes(filters.q.toLowerCase()))
    : all;

  return (
    <div>
      {data?.eligibility && <EligibilityRules eligibility={data.eligibility} onChanged={refetch} />}
      <CompFilters filters={filters} onChange={setFilters} />

      {isLoading && (
        <div className="flex items-center py-8 text-gray-600 dark:text-gray-300">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          <span className="ml-2">Loading candidates…</span>
        </div>
      )}

      {isError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
          <p className="font-bold">Could not load candidates</p>
          <button onClick={() => refetch()} className="mt-2 px-3 py-1 bg-blue-600 text-white rounded">Try again</button>
        </div>
      )}

      {!isLoading && !isError && candidates.length === 0 && (
        <div className="text-center py-10 text-gray-500 dark:text-gray-400">
          No candidates yet. Use “Sync from processor” to import File Review clients.
        </div>
      )}

      {!isLoading && !isError && candidates.length > 0 && <CandidatesTable candidates={candidates} />}
    </div>
  );
}
